import React, { FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { parse } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import { CalendarProvider } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { IconButton } from '../../atoms/iconButton/IconButton'
import { EditIcon } from '../../atoms/icons/EditIcon'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ProviderBookableCalendarSettingsProps = {
  availabilityText?: string
  events?: { startTime: string; endTime: string }[]
  onEditPressed?: () => void
  bookableTarget: number
  calendarProvider?: CalendarProvider
  hideBookableTarget?: boolean
}

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  border: `1px solid ${theme.colors.borderDefault}`,
  padding: theme.spacing['24px'],
  borderRadius: '16px',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['24px'],
}))

const TextContainer = styled.View({
  flex: 1,
})

export const ProviderBookableCalendarSettings: FunctionComponent<ProviderBookableCalendarSettingsProps> = ({
  availabilityText = '',
  events = [],
  onEditPressed,
  bookableTarget,
  calendarProvider,
  hideBookableTarget,
}) => {
  const theme = useTheme() as ThemeType
  const { formatTime } = useIntl()

  // Using separate formatTime calls because formatDateTimeRange collapses AM/PM when both times share the same period
  const startDate = parse(calendarProvider?.daily_start ?? '', 'HH:mm:ss', new Date())
  const endDate = parse(calendarProvider?.daily_end ?? '', 'HH:mm:ss', new Date())
  const workingHoursText = `${formatTime(startDate, { hour: 'numeric', minute: 'numeric' })} - ${formatTime(endDate, {
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  })}`

  return (
    <Container testID={tID('ProviderBookableCalendarSettings')}>
      <TextContainer>
        <BodyText
          color={theme.colors.textPrimary}
          text={
            <FormattedMessage
              defaultMessage='Recurring availability'
              description='Text describing availibility on template'
            />
          }
        />
        {!hideBookableTarget && (
          <BodyText
            color={theme.colors.textSecondary}
            text={
              <FormattedMessage
                defaultMessage='{current} of {target} calendar spots: availability on {availabilityText}'
                description='Text describing how many recurring calendar spots are set and which days of the week the provider is available'
                values={{ current: events.length, target: bookableTarget, availabilityText }}
              />
            }
          />
        )}
        <BodyText color={theme.colors.textSecondary} text={workingHoursText} />
      </TextContainer>
      <IconButton
        accessibilityLabel={<FormattedMessage defaultMessage='Edit' description='Button to edit calendar' />}
        onPress={() => onEditPressed?.()}
        iconColor={theme.colors.iconActive}
        leftIcon={<EditIcon fillColor={theme.colors.iconActive} size={20} isFilled={false} />}
        tabIndex={0}
        testID={tID('ProviderBookableCalendarSettings-edit-button')}
      />
    </Container>
  )
}
